import { useQuery, useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRef } from "react";

import Layout from "../components/layouts/layout.comp";
import couponCore from "../core/coupon.core";
import AddLink from "../components/form/addLink.comp";
import PencilIcon from "../icons/pencil.icon";
import TrashIcon from "../icons/trash.icon";
import { useTranslation } from "../core/translation.core";
import SolidBtn from "../components/form/solidBtn.comp";
import SolidWeakBtn from "../components/form/solidWeakBtn.comp";
import { Coupon } from "../types/coupon";

export default function CouponPage() {
  const { t, msg } = useTranslation();
  return (
    <Layout title={t(msg.coupon.coupon)} titleBtn={<AddLink id="coupon-create" href="/coupon/create" />}>
      <section className="bg-white dark:bg-her-darkSectionBg shadow-sm py-2 mb-6 rounded-2xl dark:text-slate-300">
        <CouponsTable />
      </section>
    </Layout>
  );
}

const CouponsTable = () => {
  const { t, msg } = useTranslation();
  const query = useQuery({
    queryKey: ["coupons"],
    queryFn: couponCore.loadCoupons,
  });

  if (!query) return null;

  return (
    <div>
      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_120px] gap-6 px-6 py-3 text-sm font-medium">
        <div>{t(msg.category.code)}</div>
        <div>{t(msg.ship.type)}</div>
        <div>
          {t(msg.coupon.discountAmount)} / {t(msg.coupon.discountPercentage)}
        </div>
        <div>{t(msg.coupon.expiration)}</div>
        <div></div>
      </div>
      {query?.data?.map((coupon) => (
        <CouponRow key={coupon._id} coupon={coupon} />
      ))}
    </div>
  );
};

const CouponRow = ({ coupon }: { coupon: Coupon }) => {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const deleteDialogRef = useRef<HTMLDialogElement>(null);

  function showDeleteDialog() {
    deleteDialogRef.current?.showModal();
  }

  async function confirmDelete() {
    deleteDialogRef.current?.close();
    await couponCore.deleteCoupon(coupon._id);
    queryClient.invalidateQueries({ queryKey: ["coupons"] });
  }

  const DeleteDialog = () => {
    return (
      <dialog ref={deleteDialogRef} className="dark:bg-her-blackOlive dark:text-white p-10 rounded-lg">
        <h1 className="text-xl mb-4">{t(msg.coupon.confirmDelete)}</h1>
        <p className="mb-2">
          {t(msg.coupon.code)}: <strong>{coupon.code}</strong>
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t(msg.coupon.confirmDeleteMessage)}</p>
        <div className="flex gap-4 mt-6">
          <SolidBtn onClick={() => confirmDelete()}>{t(msg.user.confirm)}</SolidBtn>
          <SolidWeakBtn onClick={() => deleteDialogRef.current?.close()}>{t(msg.form.cancel)}</SolidWeakBtn>
        </div>
      </dialog>
    );
  };

  return (
    <>
      <div
        className="grid grid-cols-[1fr_1fr_1fr_1fr_120px] gap-6 px-6 py-3 text-sm font-medium border-t-[1px] items-center"
        data-code={coupon.code}
      >
        <div>{coupon.code}</div>
        <div>{coupon.discount.amt ? t(msg.discount.fixedAmt) : t(msg.discount.percentage)}</div>
        <div>{coupon.discount.amt ?? coupon.discount.per}</div>
        <div>{coupon.expiration.date ? coupon.expiration.date : getDayName(coupon.expiration.day, t, msg)}</div>
        <div className="flex gap-2 items-center">
          <Link href={`coupon/edit/?id=${coupon._id}`} className="edit-link">
            <PencilIcon className="w-4 h-4 text-her-robinEggBlue" />
          </Link>
          <button onClick={showDeleteDialog} className="delete-btn" title={t(msg.form.delete)}>
            <TrashIcon className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
      <DeleteDialog />
    </>
  );
};

function getDayName(dayNumber?: number, t?: (msg: any) => string, msg?: any) {
  if (!dayNumber || !t || !msg) return;

  const days = [
    msg.general.sunday,
    msg.general.monday,
    msg.general.tuesday,
    msg.general.wednesday,
    msg.general.thursday,
    msg.general.friday,
    msg.general.saturday,
  ];

  return t(days[dayNumber - 1]);
}

function Th({ children }) {
  return <th className="border border-slate-600">{children}</th>;
}

function Td({ children }) {
  return <td className="border border-slate-700">{children}</td>;
}
